import { CartUpdateEvent } from '@theme/events';
import { eligible, fingerprint, replacePack, UpgradeError } from '@theme/ta-cart-upgrade-core';

const hosts = new WeakMap();
const hostSnapshots = new WeakMap();
const dismissed = new Set();
let busy = false;
let nativePending = 0;
let undo = null;
let notice = null;
let policyDialog = null;
const formatText = (text, values = {}) => Object.entries(values).reduce((s, [k, v]) => s.replaceAll(`[${k}]`, v), text);
const keyFor = (data, pair) => `${data.country}:${data.currency}:${pair.one.id}`;
const money = (data, amount) => new Intl.NumberFormat(data.locale, {
  style: 'currency', currency: data.currency,
}).format(amount / 100);
function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
function button(label, action) {
  const node = element('button', 'ta-upgrade-link', label);
  node.type = 'button'; node.dataset.taUpgradeAction = action;
  return node;
}
function render(host, data) {
  renderContent(host, data);
  hostSnapshots.set(host, {
    raw: host.querySelector('[data-ta-upgrade-data]')?.textContent,
    firstChild: host.querySelector('[data-ta-upgrade-content]')?.firstChild,
  });
}
function renderContent(host, data) {
  const content = host.querySelector('[data-ta-upgrade-content]');
  if (!content) return;
  content.replaceChildren();
  const t = data.labels;
  // A market rescue requires the customer to fix their market before seeing an upgrade.
  if (document.querySelector('.ta-cart-rescue:not([hidden])')) return;
  if (notice?.country === data.country) {
    const message = element('div', 'ta-upgrade-message', t[notice.code]);
    message.setAttribute('role', notice.code === 'success' ? 'status' : 'alert');
    content.append(message);
    if (notice.code === 'uncertain') {
      content.append(button(t.refresh, 'reload'));
      return;
    }
  }
  if (undo && undo.country === data.country && fingerprint(data.cart) === undo.fingerprint) {
    const success = element('div', 'ta-upgrade-success');
    success.append(element('strong', '', t.success), element('p', '', t.guarantee), button(t.undo, 'undo'));
    content.append(success);
  }
  for (const pair of data.catalog) {
    const source = eligible(data.cart, pair);
    if (!source) continue;
    const wrapper = element('section', 'ta-upgrade-option');
    wrapper.dataset.pair = String(pair.one.id);
    if (dismissed.has(keyFor(data, pair))) {
      wrapper.append(button(`${t.reopen} · ${t[pair.color]}`, 'reopen'));
    } else {
      const card = element('div', 'ta-upgrade-card');
      const head = element('div', 'ta-upgrade-heading');
      const percentage = new Intl.NumberFormat(data.locale, { maximumFractionDigits: 1 }).format((1 - pair.two.price / (2 * pair.one.price)) * 100);
      head.append(element('strong', '', `${t.label} · ${t[pair.color]}`), element('span', 'ta-upgrade-badge', formatText(t.save, { percent: percentage })));
      const average = `${pair.two.price % 2 ? '≈ ' : ''}${money(data, Math.round(pair.two.price / 2))}`;
      card.append(head, element('h3', '', t.title), element('p', 'ta-upgrade-benefit', t.benefit));
      const price = element('div', 'ta-upgrade-price');
      price.append(element('strong', '', formatText(t.unit, { price: average })), element('p', '', formatText(t.compare, { price: money(data, pair.one.price) })));
      card.append(price, element('p', 'ta-upgrade-total', formatText(t.total, { price: money(data, pair.two.price) })));
      const guarantee = element('details', 'ta-upgrade-guarantee');
      const summary = element('summary');
      summary.append(element('strong', '', t.guarantee), element('span', '', t.used));
      const policyButton = button(t.policy, 'policy'); policyButton.setAttribute('aria-haspopup', 'dialog');
      guarantee.append(summary, element('p', '', t.reassurance), policyButton);
      const cta = button(formatText(source.quantity === 2 ? t.switch : t.upgrade, {
        price: money(data, source.quantity === 2 ? pair.one.price * 2 - pair.two.price : pair.two.price - pair.one.price),
      }), 'upgrade');
      cta.className = 'ta-upgrade-primary';
      card.append(guarantee, cta, button(source.quantity === 2 ? t.keep_two : t.keep, 'keep'));
      wrapper.append(card);
    }
    content.append(wrapper);
  }
}
function init() {
  document.querySelectorAll('[data-ta-upgrade]').forEach(host => {
    const raw = host.querySelector('[data-ta-upgrade-data]')?.textContent;
    const snapshot = hostSnapshots.get(host);
    if (snapshot?.raw === raw && snapshot.firstChild === host.querySelector('[data-ta-upgrade-content]')?.firstChild) return;
    try {
      const data = JSON.parse(raw);
      if (!data.currency || data.currency !== data.cart.currency) return;
      hosts.set(host, data); render(host, data);
    } catch { /* Fail closed. Normal checkout remains usable. */ }
  });
}
function renderAll() {
  init();
  document.querySelectorAll('[data-ta-upgrade]').forEach(host => { if (hosts.has(host)) render(host, hosts.get(host)); });
}
async function request(url, body, accept = 'application/json') {
  const response = await fetch(url, {
    method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
    headers: { Accept: accept, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new Error('request');
  return response.json();
}
function apiFor(data) {
  return {
    read: () => request(`${data.root}/cart.js`),
    add: (id, quantity) => request(`${data.root}/cart/add.js`, { items: [{ id, quantity }] }),
    change: (id, quantity) => request(`${data.root}/cart/change.js`, { id, quantity }),
  };
}
async function refresh(data, cart) {
  const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(n => n.dataset.sectionId))];
  const sections = {};
  for (let i = 0; i < ids.length; i += 5) {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.slice(i, i + 5).join(','));
    // On product URLs, Accept: application/json selects product JSON instead of sections.
    const group = await request(url.href, undefined, '*/*');
    if (Object.values(group).some(html => typeof html !== 'string')) throw new Error('sections');
    Object.assign(sections, group);
  }
  document.dispatchEvent(new CartUpdateEvent(cart, 'ta-cart-upgrade', { source: 'ta-cart-upgrade', itemCount: cart.item_count, sections }));
  await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  renderAll();
}
function lock(value) {
  busy = value;
  document.documentElement.classList.toggle('ta-upgrade-busy', value);
  document.querySelectorAll('cart-items-component').forEach(node => {
    node.inert = value;
    if (value) node.setAttribute('aria-busy', 'true'); else node.removeAttribute('aria-busy');
  });
}
async function run(host, data, pair, isUndo, clicked) {
  if (busy || Date.now() < nativePending || document.querySelector('.cart-items-disabled')) return;
  if (document.querySelector('.ta-cart-rescue:not([hidden])')) return;
  const api = apiFor(data);
  const oldUndo = undo;
  lock(true); notice = null;
  if (clicked) clicked.textContent = data.labels.updating;
  try {
    const cart = await api.read();
    let source, target, quantity;
    if (isUndo) {
      if (!oldUndo || oldUndo.country !== data.country || fingerprint(cart) !== oldUndo.fingerprint) throw new UpgradeError('changed');
      source = cart.items.find(i => i.key === oldUndo.targetKey);
      target = oldUndo.pair.one; quantity = oldUndo.quantity;
    } else {
      if (fingerprint(cart) !== fingerprint(data.cart)) throw new UpgradeError('changed');
      source = eligible(cart, pair); target = pair.two; quantity = 1;
    }
    if (!source) throw new UpgradeError('changed');
    const product = await request(target.url);
    const live = product.variants.find(v => v.id === target.id);
    if (!live?.available || live.price !== target.price) throw new UpgradeError('changed');
    const after = await replacePack(api, cart, source.key, target, quantity);
    undo = isUndo ? null : { country: data.country, pair, quantity: source.quantity,
      targetKey: after.items.find(i => i.variant_id === target.id).key, fingerprint: fingerprint(after) };
    if (isUndo) dismissed.delete(keyFor(data, oldUndo.pair));
    await refresh(data, after);
  } catch (error) {
    undo = null;
    // An unclassified network error may be before or after mutation: never promise the original cart remains.
    notice = { country: data.country, code: error instanceof UpgradeError ? error.code : 'uncertain' };
    try { await refresh(data, await api.read()); } catch { renderAll(); }
  } finally {
    lock(false);
    const liveHost = [...document.querySelectorAll('[data-ta-upgrade]')].find(n => n.getClientRects().length && hosts.get(n)?.country === data.country);
    const focusTarget = liveHost?.querySelector('[role="alert"], .ta-upgrade-success, [data-ta-upgrade-action="upgrade"]');
    if (focusTarget) { focusTarget.tabIndex = -1; focusTarget.focus({ preventScroll: false }); }
  }
}
function openPolicy(data, opener) {
  if (policyDialog?.isConnected) return;
  const t = data.labels;
  const dialog = element('dialog', 'ta-upgrade-policy');
  policyDialog = dialog;
  const heading = element('h2', '', t.guarantee); heading.id = 'ta-upgrade-policy-title';
  dialog.setAttribute('aria-labelledby', heading.id);
  const close = button('×', ''); close.setAttribute('aria-label', t.close); close.autofocus = true;
  const header = element('header'); header.append(heading, close);
  const body = element('div', 'ta-upgrade-policy-body');
  body.append(element('p', '', t.reassurance), element('p', '', t.policy_intro));
  const details = element('details'); details.append(element('summary', '', t.full_policy));
  const full = element('div', 'ta-upgrade-policy-full'); details.append(full);
  let loaded = false, loading = false;
  async function loadPolicy() {
    if (loaded || loading) return;
    loading = true; full.textContent = t.loading;
    try {
      const response = await fetch(`${data.root}/policies/refund-policy`, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('policy');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const policy = parsed.querySelector('.shopify-policy__body');
      if (!policy?.textContent.trim()) throw new Error('policy');
      // Keep the policy prose without importing page scripts, injected widgets or navigation.
      const clean = element('div');
      const blocks = policy.querySelectorAll('h2,h3,h4,p,li');
      for (const block of blocks) {
        const p = element(/^H/.test(block.tagName) ? 'h3' : 'p', '', block.textContent.trim());
        if (p.textContent) clean.append(p);
      }
      if (!clean.childElementCount) clean.textContent = policy.textContent.trim();
      full.replaceChildren(clean); loaded = true;
    } catch {
      const retry = button(t.retry, ''); retry.onclick = loadPolicy;
      full.replaceChildren(element('p', '', t.policy_error), retry);
    } finally { loading = false; }
  }
  details.addEventListener('toggle', () => { if (details.open) loadPolicy(); });
  const back = button(t.back, ''); back.className = 'ta-upgrade-primary';
  back.onclick = close.onclick = () => dialog.close();
  body.append(details, back); dialog.append(header, body);
  // Append inside an existing drawer dialog to avoid Horizon's focus trap pulling focus behind this modal.
  (opener.closest('dialog') || document.body).append(dialog);
  dialog.addEventListener('close', () => { dialog.remove(); policyDialog = null; if (opener.isConnected) opener.focus({ preventScroll: true }); });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
  });
  dialog.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); dialog.close(); }
  });
  dialog.showModal();
}
// Capture prevents form submit, rapid quantity clicks and market rescue while replacement is in flight.
for (const type of ['click', 'submit', 'change']) document.addEventListener(type, event => {
  if (busy && event.target instanceof Element && event.target.closest('cart-items-component, .cart__ctas, product-form-component, .shopify-payment-button')) {
    event.preventDefault(); event.stopImmediatePropagation();
  }
}, true);
document.addEventListener('quantity-selector:update', () => { nativePending = Date.now() + 2000; });
document.addEventListener('cart:update', event => {
  if (event.detail?.data?.source !== 'ta-cart-upgrade') { undo = null; notice = null; nativePending = 0; }
});
document.addEventListener('discount:update', () => { undo = null; notice = null; nativePending = Date.now() + 2000; });
document.addEventListener('click', event => {
  const action = event.target.closest?.('[data-ta-upgrade-action]');
  if (!action || busy) return;
  const host = action.closest('[data-ta-upgrade]'); const data = hosts.get(host);
  if (!data) return;
  const type = action.dataset.taUpgradeAction;
  const pair = data.catalog.find(p => String(p.one.id) === action.closest('[data-pair]')?.dataset.pair);
  if (type === 'policy') return openPolicy(data, action);
  if (type === 'reload') return window.location.reload();
  if (type === 'undo') return run(host, data, null, true, action);
  if (!pair) return;
  if (type === 'upgrade') return run(host, data, pair, false, action);
  if (type === 'keep') dismissed.add(keyFor(data, pair));
  if (type === 'reopen') dismissed.delete(keyFor(data, pair));
  renderAll();
  const replacement = host.querySelector(`[data-pair="${pair.one.id}"] [data-ta-upgrade-action="${type === 'keep' ? 'reopen' : 'upgrade'}"]`);
  replacement?.focus();
});
const observer = new MutationObserver(records => {
  init();
  if (records.some(r => r.type === 'attributes' && r.target.matches?.('.ta-cart-rescue'))) renderAll();
});
observer.observe(document.documentElement, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
window.addEventListener('pageshow', () => { undo = null; notice = null; renderAll(); });
document.addEventListener('shopify:section:load', init);
init();
